import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router";
import AuthorComments from "../../components/author-comments/author-comments";
import AuthorPolls from "../../components/author-polls/author-polls";
import LineupsDisplay from "../../components/lineups-display/lineups-display";
import ManualLineups from "../../components/manual-lineups/manual-lineups";
import { useAuthorComments } from "../../hooks/use-author-comments";
import { useBroadcastScore } from "../../hooks/use-broadcast-score";
import { useCards } from "../../hooks/use-cards";
import { useConnectedPlatforms } from "../../hooks/use-connected-platforms";
import { useCurrentUser } from "../../hooks/use-current-user";
import { useGoalScorers } from "../../hooks/use-goal-scorers";
import { useLineups } from "../../hooks/use-lineups";
import { usePolls } from "../../hooks/use-polls";
import { useSubstitutions } from "../../hooks/use-substitutions";
import { useViewerCount } from "../../hooks/use-viewer-count";
import { authFetch } from "../../lib/auth-fetch";
import {
	endBroadcastRequest,
	resetBroadcast,
} from "../../redux/broadcast/broadcast.actions";
import {
	selectAwayTeam,
	selectBroadcastId,
	selectFixtureId,
	selectFullTimeAt,
	selectHalfTimeAt,
	selectHomeTeam,
	selectSecondHalfStartTime,
	selectStartTime,
	selectTimeCorrection,
} from "../../redux/broadcast/broadcast.selectors";
import BroadcastControls from "../broadcast-controls/broadcast-controls";
import BroadcastFeed from "../feed/broadcast-feed";
import { BroadcastPortalProvider } from "./broadcast-portal.context";
import * as CSS from "./broadcast-portal.styles";
import type { TabIds } from "./broadcast-portal.types";
import ChatModule from "./modules/chat-module/chat-module";
import FeedModule from "./modules/feed-module/feed-module";
import FormationsModule from "./modules/formations-module/formations-module";
import InteractionsModule from "./modules/interactions-module/intereactions-module";
import PollsModule from "./modules/polls-module/polls-module";
import SubsModule from "./modules/subs-module/subs-module";
import TeamsModule from "./modules/teams-module/teams-module";
import UpdatesModule from "./modules/updates-module/updates-module";

const BroadcastPortal = () => {
	const [activeTeam, setActiveTeam] = useState<TabIds>("homeTeam");

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [confirming, setConfirming] = useState(false);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [fetchingLineups, setFetchingLineups] = useState(false);
	const [lineupFetchMsg, setLineupFetchMsg] = useState<string | null>(null);
	const [lastLineupAttempt, setLastLineupAttempt] = useState<Date | null>(null);
	const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lineupRetryRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const homeTeam = useSelector(selectHomeTeam);
	const awayTeam = useSelector(selectAwayTeam);
	const broadcastId = useSelector(selectBroadcastId);
	const startTime = useSelector(selectStartTime);
	const fixtureId = useSelector(selectFixtureId);
	const halfTimeAt = useSelector(selectHalfTimeAt);
	const secondHalfStartTime = useSelector(selectSecondHalfStartTime);
	const fullTimeAt = useSelector(selectFullTimeAt);
	const timeCorrection = useSelector(selectTimeCorrection);
	const lineups = useLineups(fixtureId);
	const { homeScore, awayScore, varReview } = useBroadcastScore(broadcastId);
	const substitutions = useSubstitutions(broadcastId);

	const TeamsContext = createContext({ setActiveTeam, activeTeam });

	// Keep authorLastSeen fresh so the auto-end scheduler knows the author is present
	useEffect(() => {
		if (!broadcastId) return;
		const ping = () =>
			authFetch(`/api/broadcasts/${broadcastId}/heartbeat`, {
				method: "POST",
			}).catch(() => {});
		ping();
		const interval = setInterval(ping, 60_000);
		return () => clearInterval(interval);
	}, [broadcastId]);

	useEffect(
		() => () => {
			if (lineupRetryRef.current) clearInterval(lineupRetryRef.current);
		},
		[],
	);

	const doFetchLineups = async (): Promise<boolean> => {
		setFetchingLineups(true);
		setLineupFetchMsg(null);
		setLastLineupAttempt(new Date());
		try {
			const res = await authFetch(
				`/api/broadcasts/${broadcastId}/fetch-lineups`,
				{
					method: "POST",
				},
			);
			const data = (await res.json()) as { found: boolean };
			if (data.found) {
				setLineupFetchMsg("Lineups fetched!");
				if (lineupRetryRef.current) clearInterval(lineupRetryRef.current);
				setFetchingLineups(false);
				return true;
			}
			setLineupFetchMsg("Not available yet — retrying every 2 min…");
		} catch {
			setLineupFetchMsg("Request failed — retrying every 2 min…");
		}
		cooldownRef.current = setTimeout(() => setFetchingLineups(false), 30_000);
		return false;
	};

	const handleFetchLineups = () => {
		if (lineupRetryRef.current) clearInterval(lineupRetryRef.current);
		doFetchLineups().then((found) => {
			if (!found) {
				lineupRetryRef.current = setInterval(
					() => {
						doFetchLineups().then((ok) => {
							if (ok && lineupRetryRef.current)
								clearInterval(lineupRetryRef.current);
						});
					},
					2 * 60 * 1000,
				);
			}
		});
	};
	const cards = useCards(broadcastId);
	const goalScorers = useGoalScorers(broadcastId);
	const viewerCount = useViewerCount(broadcastId);
	const { bluesky: hasBluesky, blueskyHandle } = useConnectedPlatforms();
	const polls = usePolls(broadcastId);
	const currentUser = useCurrentUser();
	const comments = useAuthorComments(broadcastId, currentUser?.uid ?? null);
	const [activeTab, setActiveTab] = useState<"feed" | "polls" | "comments">(
		"feed",
	);

	if (!homeTeam || !awayTeam || !broadcastId) {
		return <Navigate to="/new" replace />;
	}

	const viewerUrl = `${window.location.origin}/broadcast/${broadcastId}`;

	return (
		<CSS.BroadcastPortal>
			<BroadcastPortalProvider>
				<CSS.SideColumn>
					<TeamsModule />
					{/* <FormationsModule /> */}
				</CSS.SideColumn>
				<CSS.CentralColumn>
					<CSS.CentralUpper>
						<SubsModule />
						<InteractionsModule />
					</CSS.CentralUpper>
					<CSS.CentralLower>
						<UpdatesModule />
					</CSS.CentralLower>
				</CSS.CentralColumn>
				<FeedModule />
			</BroadcastPortalProvider>
			{/* <h4>
				{homeTeam} {homeScore} – {awayScore} {awayTeam}
			</h4>
			<p>
				Viewer link:{" "}
				<a href={viewerUrl} target="_blank" rel="noreferrer">
					{viewerUrl}
				</a>{" "}
				— {viewerCount} {viewerCount === 1 ? "viewer" : "viewers"} live
			</p>
			{confirming ? (
				<div>
					<span>End this broadcast? Viewers will see it as finished.</span>
					<button
						type="button"
						onClick={() => dispatch(endBroadcastRequest({ broadcastId }))}
					>
						Confirm
					</button>
					<button type="button" onClick={() => setConfirming(false)}>
						Cancel
					</button>
				</div>
			) : (
				<button type="button" onClick={() => setConfirming(true)}>
					End Broadcast
				</button>
			)}
			{confirmingDelete ? (
				<div>
					<span>Permanently delete this broadcast? This cannot be undone.</span>
					<button
						type="button"
						onClick={async () => {
							await authFetch(`/api/broadcasts/${broadcastId}`, {
								method: "DELETE",
							});
							dispatch(resetBroadcast());
							navigate("/");
						}}
					>
						Delete
					</button>
					<button type="button" onClick={() => setConfirmingDelete(false)}>
						Cancel
					</button>
				</div>
			) : (
				<button type="button" onClick={() => setConfirmingDelete(true)}>
					Delete broadcast
				</button>
			)}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr",
					gap: "1.5rem",
					alignItems: "start",
				}}
			>
				<div>
					<BroadcastControls />
				</div>
				<div>
					{fixtureId && !lineups && (
						<div>
							<button
								type="button"
								onClick={handleFetchLineups}
								disabled={fetchingLineups}
							>
								{fetchingLineups
									? "Fetching…"
									: lineupRetryRef.current
										? "Retrying…"
										: "Fetch Lineups"}
							</button>
							{lineupFetchMsg && <span> {lineupFetchMsg}</span>}
							{lastLineupAttempt && (
								<span
									style={{ marginLeft: 8, color: "#888", fontSize: "0.85em" }}
								>
									Last tried {lastLineupAttempt.toLocaleTimeString()}
								</span>
							)}
							<ManualLineups
								broadcastId={broadcastId}
								homeTeam={homeTeam}
								awayTeam={awayTeam}
							/>
						</div>
					)}
					{lineups && (
						<LineupsDisplay
							lineups={lineups}
							substitutions={substitutions}
							cards={cards}
							scorers={goalScorers}
						/>
					)}
				</div>
				<div>
					<div
						style={{
							display: "flex",
							gap: 0,
							borderBottom: "1px solid #ccc",
							marginBottom: 12,
						}}
					>
						{(["feed", "polls", "comments"] as const).map((tab) => (
							<button
								key={tab}
								type="button"
								onClick={() => setActiveTab(tab)}
								style={{
									padding: "6px 16px",
									border: "none",
									borderBottom:
										activeTab === tab
											? "2px solid #000"
											: "2px solid transparent",
									background: "none",
									fontWeight: activeTab === tab ? "bold" : "normal",
									cursor: "pointer",
									textTransform: "capitalize",
								}}
							>
								{tab}
								{tab === "polls" &&
									polls.filter((p) => !p.ended).length > 0 && (
										<span
											style={{
												marginLeft: 4,
												background: "#e00",
												color: "#fff",
												borderRadius: 8,
												fontSize: "0.75em",
												padding: "1px 5px",
											}}
										>
											{polls.filter((p) => !p.ended).length}
										</span>
									)}
								{tab === "comments" &&
									comments.filter((c) => !c.read).length > 0 && (
										<span
											style={{
												marginLeft: 4,
												background: "#e00",
												color: "#fff",
												borderRadius: 8,
												fontSize: "0.75em",
												padding: "1px 5px",
											}}
										>
											{comments.filter((c) => !c.read).length}
										</span>
									)}
							</button>
						))}
					</div>
					{activeTab === "feed" && (
						<BroadcastFeed
							broadcastId={broadcastId}
							startTime={startTime}
							halfTimeAt={halfTimeAt}
							secondHalfStartTime={secondHalfStartTime}
							fullTimeAt={fullTimeAt}
							timeCorrection={timeCorrection}
							varReview={varReview}
							homeTeam={homeTeam}
							awayTeam={awayTeam}
							canShareToBluesky={hasBluesky}
							blueskyHandle={blueskyHandle}
						/>
					)}
					{activeTab === "polls" && (
						<AuthorPolls
							broadcastId={broadcastId}
							polls={polls}
							hasBluesky={hasBluesky}
						/>
					)}
					{activeTab === "comments" && (
						<AuthorComments broadcastId={broadcastId} />
					)}
				</div>
			</div> */}
		</CSS.BroadcastPortal>
	);
};

export default BroadcastPortal;
